import { Attribute, QuickRankConfiguration, QuickRankMode } from "../../gql/graphql";
import { CardType } from "../../types";

import actionHandlers from "./handlers/normal";
import actionHandlersOptimizedMode from "./handlers/optimized";

export enum QuickRankRenderableDestinations {
  DECK = "deck",
  HAND = "hand",
  // Currently rated card
  RATED = "rated",
}

export enum QuickRankNonRenderableDestinations {
  // List of cards that we generate
  ALL = "all",
  // List of cards that we have to rate at the end of the game
  TO_RATE = "toRate",
}

export type QuickRankDestinations = QuickRankNonRenderableDestinations | QuickRankRenderableDestinations;

export type QuickRankCardsData = {
  [key in QuickRankDestinations]: (CardType | null)[];
};

export interface QuickRankGameConfiguration extends QuickRankConfiguration {
  numberOfHands: number;
  cardsPerHand: number;
}

export interface RatingType {
  attribute: Attribute;
  score: number;
}

export interface OptimizedModeMatrix {
  currentLayer: (CardType | null)[][];
  nextLayer: (CardType | null)[][];
  checkedIndexes: [number, number];
}

export interface QuickRankAttributeResult {
  count: number;
  rank: number;
  rating: number;
}

export type QuickRankGameResults = {
  [key in string]: QuickRankAttributeResult;
};

export interface QuickRankState {
  configuration: QuickRankGameConfiguration;
  cards: QuickRankCardsData;
  rating: RatingType[];
  results: QuickRankGameResults;
  status: {
    round: number;
    isPreviewModalOpen: boolean;
    shouldChangeRound: boolean;
    isGameFinished: boolean;
    isRatingRejectedCards: boolean;
    optimizedModeMatrix: OptimizedModeMatrix | null;
    displayCurrentIndex: number | null;
    startTime: Date;
  };
}

export enum QuickRankActionName {
  NEXT_ROUND = "NEXT_ROUND",
  SELECT_CARD = "SELECT_CARD",
  RATE_CARD = "RATE_CARD",
  DISPLAY_CARD = "DISPLAY_CARD",
  CLOSE_PREVIEW_MODAL = "CLOSE_PREVIEW_MODAL",
}

export interface QuickRankActionHandler {
  [QuickRankActionName.NEXT_ROUND]: (state: QuickRankState) => QuickRankState;
  [QuickRankActionName.SELECT_CARD]: (state: QuickRankState, card: CardType) => QuickRankState;
  [QuickRankActionName.RATE_CARD]: (state: QuickRankState, ratingScore: number) => QuickRankState;
}

export interface QuickRankStateOptimizedMode extends QuickRankState {
  configuration: QuickRankGameConfiguration & { mode: QuickRankMode.Optimized };
  status: {
    round: number;
    isPreviewModalOpen: boolean;
    shouldChangeRound: boolean;
    isGameFinished: boolean;
    isRatingRejectedCards: boolean;
    displayCurrentIndex: number;
    startTime: Date;
    optimizedModeMatrix: OptimizedModeMatrix;
  };
}

export type QuickRankAction =
  | { type: QuickRankActionName.NEXT_ROUND }
  | { type: QuickRankActionName.SELECT_CARD; payload: { card: { index: number; attribute: Attribute } } }
  | { type: QuickRankActionName.RATE_CARD; payload: { ratingScore: number } }
  | { type: QuickRankActionName.DISPLAY_CARD; payload: { index: number | null } }
  | { type: QuickRankActionName.CLOSE_PREVIEW_MODAL };

export type QuickRankReducerType = (state: QuickRankState, action: QuickRankAction) => QuickRankState;

export const quickRankReducer: QuickRankReducerType = (state, action) => {
  const actions = state.configuration.mode === QuickRankMode.Optimized ? actionHandlersOptimizedMode : actionHandlers;

  switch (action.type) {
    case QuickRankActionName.NEXT_ROUND: {
      return actions[QuickRankActionName.NEXT_ROUND](state);
    }
    case QuickRankActionName.SELECT_CARD: {
      return actions[QuickRankActionName.SELECT_CARD](state, action.payload.card);
    }
    case QuickRankActionName.RATE_CARD: {
      return actions[QuickRankActionName.RATE_CARD](state, action.payload.ratingScore);
    }
    case QuickRankActionName.CLOSE_PREVIEW_MODAL: {
      return {
        ...state,
        status: {
          ...state.status,
          isPreviewModalOpen: false,
          startTime: new Date(),
        },
      };
    }
    case QuickRankActionName.DISPLAY_CARD: {
      return {
        ...state,
        status: {
          ...state.status,
          displayCurrentIndex: action.payload.index,
        },
      };
    }
    default:
      return state;
  }
};
